import { useRef, useState } from 'react';
import { Sheet } from '../components/Sheet';
import { useT } from '../i18n/I18nProvider';
import { updatePackageWeight } from '../api/endpoints';

interface Props {
  code: string;
  shipmentNumber: string;
  onClose: () => void;
  onSaved: (weightKg: number) => void;
}

/**
 * Oprava váhy (F3.7). Přeneseno z původní aplikace, jen do bottom sheetu
 * a s validací, která pouští čárku i tečku – skladník píše, jak je zvyklý.
 *
 * Fotky jdou přímo na server, ne přes offline frontu: multipart s několika
 * snímky by ve frontě zabral desítky MB a IndexedDB by to odneslo dřív
 * než hlášení, na kterých záleží víc.
 */
export function WeightSheet({ code, shipmentNumber, onClose, onSaved }: Props) {
  const { t } = useT();
  const [value, setValue] = useState('');
  const filesRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    const weight = Number.parseFloat(value.trim().replace(',', '.'));
    if (!Number.isFinite(weight) || weight <= 0) {
      setError(t('errWeight'));
      return;
    }

    setError(null);
    setSaving(true);
    try {
      await updatePackageWeight({ code, newWeightKg: weight, files: filesRef.current?.files ?? null });
      onSaved(weight);
    } catch (err) {
      console.error('[weight] uložení váhy selhalo:', err);
      setError(t('sendErr'));
      setSaving(false);
    }
  }

  return (
    <Sheet title={t('weightTitle')} meta={shipmentNumber} onClose={onClose}>
      <div className="note-text">{t('weightHint')}</div>

      <div className="lbl">{t('weightNew')}</div>
      <input
        className="qinput"
        inputMode="decimal"
        placeholder={t('weightPh')}
        value={value}
        aria-label={t('weightNew')}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
        }}
      />

      <div className="lbl" style={{ marginTop: 12 }}>
        {t('weightPhotos')}
      </div>
      <input ref={filesRef} type="file" accept="image/*" multiple aria-label={t('weightPhotos')} />

      {error && <div className="err">{error}</div>}

      <div className="row">
        <button className="btn primary" type="button" disabled={saving} onClick={() => void save()}>
          {saving ? t('sending') : t('weightSave')}
        </button>
        <button className="btn ghost" type="button" onClick={onClose}>
          {t('cancel')}
        </button>
      </div>
    </Sheet>
  );
}
